import React, { useEffect, useState } from 'react';
import { Dialog, DialogActions, DialogContent, DialogTitle, Button, IconButton, Typography, Box, TextField, InputAdornment, Avatar, MenuItem, Select, BaseTextFieldProps } from '@mui/material';
import Grid from '@mui/material/Grid2';
import { CountryIso2, defaultCountries, FlagImage, parseCountry, usePhoneInput } from 'react-international-phone';
import 'react-international-phone/style.css';
import { toast } from 'react-toastify';

// Import local components and services
import { ModalClose } from '../../assets/icons';
import { updateAddonAccountRequest } from '../../types/auth';
import AuthService from '../../services/auth.service';

interface EditWorkspaceProps {
  open: boolean;
  onClose: () => void;
  workspaceData?: {
    userId: string;
    companyId: string;
    companyName: string;
    email: string;
    firstName: string;
    lastName: string;
    name: string;
    parentCompanyId: string;
    jobTitle?: string;
    phoneNumber: string;
    picture?: string;
  };
  onConfirm: () => void;
}

export interface MUIPhoneProps extends BaseTextFieldProps {
    value: string;
    onChange: (phone: string) => void;
}

export const MuiPhone: React.FC<MUIPhoneProps> = ({
    value,
    onChange,
    ...restProps
}) => {
    const { inputValue, handlePhoneValueChange, inputRef, country, setCountry } =
        usePhoneInput({
            defaultCountry: 'us',
            value,
            countries: defaultCountries,
            onChange: (data) => {
                onChange(data.phone);
            },
            disableCountryGuess: true,
            forceDialCode: true,
        });

    return (
        <TextField
            variant="outlined"
            color="primary"
            placeholder="Phone number"
            value={inputValue}
            onChange={handlePhoneValueChange}
            type="tel"
            inputRef={inputRef}
            sx={{
                "& .MuiOutlinedInput-root": {
                    borderRadius: '8px',
                    borderColor: '#666666',
                },

                " .MuiInputLabel-root.Mui-focused": {
                    color: "inherit",
                },

                "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
                    borderColor: '#333333',
                    border: '1px solid'
                },

                "& .MuiOutlinedInput-input": {
                    padding: '14px 16px 16px 0',
                    background: '#ffffff',
                    borderRadius: '8px'
                },
                "&.MuiInputBase-input::placeholder": {
                    color: '#333333',
                    fontSize: '16px',
                    opacity: '0.8'
                },
            }}
            slotProps={{
                input: {
                    startAdornment: <InputAdornment
                        position="start"
                        style={{ marginRight: '2px' }}
                    >
                        <Select
                            MenuProps={{
                                style: {
                                    height: '300px',
                                    width: '360px',
                                    top: '10px',
                                    left: '-34px',
                                },
                                transformOrigin: {
                                    vertical: 'top',
                                    horizontal: 'left',
                                },
                            }}
                            sx={{
                                width: 'max-content',
                                fieldset: {
                                    display: 'none',
                                },
                                '&.Mui-focused:has(div[aria-expanded="false"])': {
                                    fieldset: {
                                        display: 'block',
                                    },
                                },
                                '.MuiSelect-select': {
                                    padding: '8px',
                                    paddingRight: '24px !important',
                                },
                                svg: {
                                    right: 0,
                                },
                            }}
                            value={country.iso2}
                            onChange={(e) => setCountry(e.target.value as CountryIso2)}
                            renderValue={(value) => (
                                <FlagImage iso2={value} style={{ display: 'flex' }} />
                            )}
                        >
                            {defaultCountries.map((c) => {
                                const country = parseCountry(c);
                                return (
                                    <MenuItem key={country.iso2} value={country.iso2}>
                                        <FlagImage
                                            iso2={country.iso2}
                                            style={{ marginRight: '8px' }}
                                        />
                                        <Typography marginRight="8px">{country.name}</Typography>
                                        <Typography color="gray">+{country.dialCode}</Typography>
                                    </MenuItem>
                                );
                            })}
                        </Select>
                    </InputAdornment>
                },
            }}
            {...restProps}
        />
    );
};

const EditWorkspace: React.FC<EditWorkspaceProps> = ({ open, onClose, workspaceData, onConfirm }) => {
  // Add form state

  console.log(workspaceData)
  const [formData, setFormData] = useState({
    fullName: workspaceData?.name || '',
    email: workspaceData?.email || '',
    companyName: workspaceData?.companyName || '',
    jobTitle: workspaceData?.jobTitle || ''
  });

  // Add error state
  const [error, setError] = useState('');

  // Add loading state
  const [isLoading, setIsLoading] = useState(false);

  // Add form validation state
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});

  // Initialize phone state with workspaceData
  const [phone, setPhone] = useState(workspaceData?.phoneNumber || '');

  // Update form data AND phone when workspaceData changes
  useEffect(() => {
    if (workspaceData) {
      setFormData({
        fullName: workspaceData.name || '',
        email: workspaceData.email || '',
        companyName: workspaceData.companyName || '',
        jobTitle: workspaceData.jobTitle || ''
      });
      // Ensure phone number is updated when workspaceData changes
      setPhone(workspaceData.phoneNumber || '');
    }
  }, [workspaceData]);

  // Handle input changes
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Clear error for the changed field
    setFormErrors(prev => ({
      ...prev,
      [name]: ''
    }));
  };

  // Validate form fields
  const validateForm = () => {
    const errors: Record<string, string> = {};
    
    if (!formData.fullName.trim()) {
      errors.fullName = 'Full name is required';
    }
    
    if (!formData.companyName.trim()) {
      errors.companyName = 'Company name is required';
    }

    if (!phone.trim()) {
      errors.phone = 'Phone number is required';
    } else if (!validatePhone(phone)) {
        errors.phone = 'Please enter a valid phone number';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Handle form submission
  const handleUpdate = async () => {
    try {
      setError('');
      
      // Store validation result in a variable to avoid multiple calls
      const isValid = validateForm();
      
      if (!isValid) {
        return;
      }

      setIsLoading(true);

      const nameParts = formData.fullName.trim().split(/\s+/);
      const firstName = nameParts[0] || '';
      const lastName = nameParts.slice(1).join(' ') || '';

      const updateRequest: updateAddonAccountRequest = {
        email: workspaceData?.email || '',
        given_name: firstName,
        family_name: lastName,
        name: formData.fullName,
        job_title: formData.jobTitle || '',
        phone_number: phone,
        company_id: workspaceData?.companyId || '',
        company_name: formData.companyName
      };

      // Call the API
      await AuthService.updateAddonAccounts(updateRequest);
      toast.success('Workspace updated successfully');
      
      onConfirm();
      handleClose();
      
    } catch (err: any) {
      toast.error(err.message || 'Failed to update workspace');
      setError(err.message || 'Failed to update workspace');
    } finally {
      setIsLoading(false);
    }
  };

  // Add phone state and handlers
  const [phoneBlurred, setPhoneBlurred] = useState(false);

  const validatePhone = (phoneNumber: string) => {
      const digitsOnly = phoneNumber.replace(/\D/g, '');
      return digitsOnly.length >= 10 && digitsOnly.length <= 15;
  };

  const handlePhoneChange = (value: string) => {
      setPhone(value);
      if (phoneBlurred) {
          if (validatePhone(value)) {
              setFormErrors(prev => ({
                  ...prev,
                  phone: ''
              }));
          }
      }
  };

  const handlePhoneBlur = () => {
      setPhoneBlurred(true);
      if (phone.trim()) {
          const isValid = validatePhone(phone);
          if (!isValid) {
              setFormErrors(prev => ({
                  ...prev,
                  phone: 'Please enter a valid phone number'
              }));
          }
      } else {
          setFormErrors(prev => ({
              ...prev,
              phone: 'Phone number is required'
          }));
      }
  };


  // Add cleanup function
  const resetForm = () => {
    if (workspaceData) {
      setFormData({
        fullName: workspaceData.name,
        email: workspaceData.email,
        companyName: workspaceData.companyName,
        jobTitle: workspaceData.jobTitle || '',
      });
      setPhone(workspaceData.phoneNumber);
    } else {
      setFormData({
        fullName: '',
        email: '',
        companyName: '',
        jobTitle: ''
      });
      setPhone('');
    }
    setFormErrors({});
    setError('');
    setPhoneBlurred(false);
  };

  // Modify onClose handler
  const handleClose = () => {
    resetForm();
    onClose();
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose}
      className='card-details-modal' 
      sx={{ '& .MuiDialog-paper': { width: '100%', maxWidth: '775px', borderRadius: '16px', border: '1px solid #E8E8EA' } }}
    >
      <DialogTitle sx={{ m: 0, }} p={{ xs: "16px 16px 15px !important" }} borderBottom="1px solid #E8EAF0">
        <Typography color='#333333' fontSize="min(24px, calc(20px + 4 * ((100vw - 320px) / (1920 - 320))))" fontWeight='500'>Edit Workspace</Typography>
        <IconButton
          aria-label="close"
          onClick={handleClose}
          sx={() => ({
            position: 'absolute',
            right: { xs: "17px" },
            top: { xs: "15px" },
          })}
        >
          <ModalClose></ModalClose>
        </IconButton>
      </DialogTitle>
      <DialogContent dividers sx={{ borderTop: 'none', borderBottom: 'none', p: { xs: "16px", sm: "25px 32px 32px" } }}>
        <Grid container rowSpacing={{ xs: 3, sm: 3, md: 3 }} columnSpacing={{ xs: 3, sm: 2, md: 3 }}>
          <Grid size={{ xs: 12, sm: 6, md: 6, lg: 6 }} display="flex" justifyContent={{ xs: "center", sm: "start" }}>
            <Box position="relative" borderRadius="100%" width="fit-content">
              <Avatar alt="" src={workspaceData?.picture || ""} sx={{ width: 102, height: 102, border: "1px solid #614EFA" }} />
            </Box>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 6, lg: 6 }}>
            <Typography color='#333333' fontSize='14px' fontWeight='500' mb='12px' sx={{ opacity: '0.6' }}>
              Full Name
            </Typography>
            <TextField
              name="fullName"
              value={formData.fullName}
              onChange={handleInputChange}
              variant="outlined"
              placeholder='Enter full name'
              required
              error={!!formErrors.fullName}
              helperText={formErrors.fullName}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: '8px',
                  borderColor: '#C7C7C7',
                },

                "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
                  borderColor: '#333333',
                  border: '1px solid'
                },

                "& .MuiOutlinedInput-input": {
                  padding: '14px 16px',
                },
                "&.MuiInputBase-input::placeholder": {
                  color: '#333333',
                  fontSize: '16px',
                  opacity: '0.8'
                },
              }}
              fullWidth
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 6, lg: 6 }}>
            <Typography color='#333333' fontSize='14px' fontWeight='500' mb='12px' sx={{ opacity: '0.6' }}>
              Email
            </Typography>
            <TextField
              name="email"
              value={formData.email}
              onChange={handleInputChange}
              variant="outlined"
              placeholder='Enter email'
              required
              disabled={true}
              error={!!formErrors.email}
              helperText={formErrors.email}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: '8px',
                  borderColor: '#C7C7C7',
                  background: "#ffffff"
                },

                "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
                  borderColor: '#333333',
                  border: '1px solid'
                },

                "& .MuiOutlinedInput-input": {
                  padding: '14px 16px 16px 0',
                  background: '#ffffff',
                  borderRadius: '8px'
                },
                "&.MuiInputBase-input::placeholder": {
                  color: '#333333',
                  fontSize: '16px',
                  opacity: '0.8'
                },
                "& .Mui-disabled": {
                  backgroundColor: "#f5f5f5",
                  WebkitTextFillColor: "#666666"
                }
              }}
              slotProps={{
                input: {
                  startAdornment: <InputAdornment position="start"><svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M17 20.5H7C4 20.5 2 19 2 15.5V8.5C2 5 4 3.5 7 3.5H17C20 3.5 22 5 22 8.5V15.5C22 19 20 20.5 17 20.5Z" stroke="#333333" strokeWidth="1.5" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round" />
                    <path d="M17 9L13.87 11.5C12.84 12.32 11.15 12.32 10.12 11.5L7 9" stroke="#333333" strokeWidth="1.5" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                  </InputAdornment>,
                },
              }}
              fullWidth
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 6, lg: 6 }}>
            <Typography color='#333333' fontSize='14px' fontWeight='500' mb='12px' sx={{ opacity: '0.6' }}>
              Company Name
            </Typography>
            <TextField
              name="companyName"
              value={formData.companyName}
              onChange={handleInputChange}
              variant="outlined"
              placeholder='Enter company name'
              required
              error={!!formErrors.companyName}
              helperText={formErrors.companyName}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: '8px',
                  borderColor: '#C7C7C7',
                },

                "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
                  borderColor: '#333333',
                  border: '1px solid'
                },

                "& .MuiOutlinedInput-input": {
                  padding: '14px 16px',
                },
                "&.MuiInputBase-input::placeholder": {
                  color: '#333333',
                  fontSize: '16px',
                  opacity: '0.8'
                },
              }}
              fullWidth
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 6, lg: 6 }}>
            <Typography color='#333333' fontSize='14px' fontWeight='500' mb='12px' sx={{ opacity: '0.6' }}>
              Job Title
            </Typography>
            <TextField
              name="jobTitle"
              value={formData.jobTitle}
              onChange={handleInputChange}
              variant="outlined"
              placeholder='Enter job title'
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: '8px',
                  borderColor: '#C7C7C7',
                },

                "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
                  borderColor: '#333333',
                  border: '1px solid'
                },

                "& .MuiOutlinedInput-input": {
                  padding: '14px 16px',
                },
                "&.MuiInputBase-input::placeholder": {
                  color: '#333333',
                  fontSize: '16px',
                  opacity: '0.8'
                },
              }}
              fullWidth
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 6, lg: 6 }}>
            <Typography color='#333333' fontSize='14px' fontWeight='500' mb='12px' sx={{ opacity: '0.6' }}>
              Phone Number
            </Typography>
            <MuiPhone
                value={phone}
                onChange={handlePhoneChange}
                onBlur={handlePhoneBlur}
                error={!!formErrors.phone}
                helperText={formErrors.phone}
                fullWidth
            />
          </Grid>
        </Grid>
        {error && (
          <Typography color="error" sx={{ mt: 2 }}>
            {error}
          </Typography>
        )}
      </DialogContent>
      <DialogActions sx={{ mb: { xs: "16px", sm: "32px" }, mr: { xs: "11px", sm: "32px" }, p: "0" }}>
        <Button onClick={handleClose} size='large' color="primary" sx={{
          border: '1px solid #E8EAF0',
          px: '22px',
          color: '#2B2B30 !important',
          textTransform: 'capitalize',
          borderRadius: '8px',
          mr: '16px',
          '&:hover': {
            border: '1px solid #E8EAF0 !important',
            boxShadow: 'unset !important'
          },
        }}>
          Cancel
        </Button>
        <Button
          variant='contained'
          size='large'
          color='primary'
          disabled={isLoading}
          onClick={handleUpdate}
          sx={{
            textTransform: 'capitalize',
            color: '#FFFFFF !important',
            px: '24px',
            borderRadius: '8px',
            '&:hover': {
              border: 'none !important'
            },
            minWidth: "94px",
            '&.Mui-disabled': {
              backgroundColor: '#E8EAF0',
              color: '#A3A3A3 !important'
            }
          }}
        >
          {isLoading ? 'Updating...' : 'Update'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditWorkspace;
